import React, { useState } from "react";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Calculator, Copy, Check, Scale, BookOpen, HelpCircle } from "lucide-react";
import { toast } from "sonner";
import {
  CalculationResult,
  calcularIndexacionIPC,
  calcularCanonesArrendamiento,
  calcularCuotaAlimentaria,
  calcularMoraPH
} from "@/lib/calculators/calculatorsEngine";
import { CALCULATOR_FAQS } from "@/lib/calculators/calculatorFaqs";
import { deductCalculatorCredit } from "@/lib/calculators/deductCalculatorCredit";

// 1. Indexación (IPC)
export const IndexacionForm: React.FC = () => {
  const [valorHistorico, setValorHistorico] = useState("25000000");
  const [fechaOrigen, setFechaOrigen] = useState("2021-01");
  const [fechaCorte, setFechaCorte] = useState("2026-08");
  const [modoAvanzado, setModoAvanzado] = useState(false);
  const [ipcInicialCustom, setIpcInicialCustom] = useState("");
  const [ipcFinalCustom, setIpcFinalCustom] = useState("");
  const [result, setResult] = useState<CalculationResult | null>(null);

  const handleCalculate = async () => {
    const success = await deductCalculatorCredit();
    if (!success) return;

    const v = parseFloat(valorHistorico) || 0;

    try {
      const response = await fetch("http://localhost:8000/api/v1/services/ipc/calculate", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          valor_historico: v,
          fecha_origen: fechaOrigen,
          fecha_corte: fechaCorte,
          ipc_inicial_custom: modoAvanzado && ipcInicialCustom ? parseFloat(ipcInicialCustom) : null,
          ipc_final_custom: modoAvanzado && ipcFinalCustom ? parseFloat(ipcFinalCustom) : null
        })
      });

      if (response.ok) {
        const resJson = await response.json();
        const data = resJson.data;

        setResult({
          title: "VALOR ACTUALIZADO A LA FECHA DE CORTE",
          total: Math.round(data.valor_actualizado),
          summary: `Actualización monetaria de $${data.valor_historico.toLocaleString("es-CO")} conforme al IPC DANE (${data.base_estadistica}) desde ${data.periodo_origen_humano} hasta ${data.periodo_corte_humano}.`,
          breakdown: [
            { label: "Valor Histórico de Origen", value: data.valor_historico },
            { label: "Fecha y Período de Origen", value: data.periodo_origen_humano },
            { label: "Índice IPC Inicial (DANE)", value: data.ipc_inicial },
            { label: "Fecha y Período de Corte", value: data.periodo_corte_humano },
            { label: "Índice IPC Final (DANE)", value: data.ipc_final },
            { label: "Base Estadística Oficial", value: data.base_estadistica },
            { label: "Factor de Actualización (IPC_fin / IPC_ini)", value: data.factor_actualizacion },
            { label: "Variación Inflacionaria Acumulada", value: `${data.variacion_porcentaje}%` },
            { label: "Incremento por Actualización Monetaria", value: Math.round(data.incremento_monetario) },
            { label: "VALOR ACTUALIZADO A LA FECHA DE CORTE", value: Math.round(data.valor_actualizado) }
          ],
          normativa: data.fundamentos_legales
        });
        toast.success("Actualización monetaria calculada por el motor oficial de la API");
        return;
      }
    } catch (e) {
      console.warn("Backend API not reached, using local engine fallback", e);
    }

    // Fallback Local TS
    const i1 = (modoAvanzado && ipcInicialCustom) ? parseFloat(ipcInicialCustom) : 106.1;
    const i2 = (modoAvanzado && ipcFinalCustom) ? parseFloat(ipcFinalCustom) : 156.4;

    const res = calcularIndexacionIPC(v, i1, i2, {
      fechaOrigen,
      fechaCorte,
      ipcInicialCustom: modoAvanzado && ipcInicialCustom ? parseFloat(ipcInicialCustom) : undefined,
      ipcFinalCustom: modoAvanzado && ipcFinalCustom ? parseFloat(ipcFinalCustom) : undefined
    });

    setResult(res);
  };

  const faqs = CALCULATOR_FAQS["indexacion"] || [];

  return (
    <div className="space-y-8">
      {/* Explanatory Key Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-slate-50 rounded-2xl border border-slate-200 p-4 space-y-1">
          <p className="text-xs font-black text-slate-900 uppercase tracking-wider">IPC DANE Oficial</p>
          <p className="text-xs text-slate-600 leading-relaxed">
            Corrección monetaria oficial basada en las series del DANE Base 2018=100.
          </p>
        </div>
        <div className="bg-slate-50 rounded-2xl border border-slate-200 p-4 space-y-1">
          <p className="text-xs font-black text-slate-900 uppercase tracking-wider">Fórmula Judicial</p>
          <p className="text-xs text-slate-600 leading-relaxed">
            Vp = Vh * (IPC_final / IPC_inicial) aplicada por la CSJ Sentencia SC-3201/2021.
          </p>
        </div>
        <div className="bg-slate-50 rounded-2xl border border-slate-200 p-4 space-y-1">
          <p className="text-xs font-black text-slate-900 uppercase tracking-wider">Diferenciación de Intereses</p>
          <p className="text-xs text-slate-600 leading-relaxed">
            La indexación restituye el poder adquisitivo sin constituir intereses moratorios ni remuneratorios.
          </p>
        </div>
      </div>

      {/* Main Form */}
      <div className="bg-white rounded-2xl border border-slate-200 p-6 space-y-6 shadow-sm">
        <h3 className="text-sm font-black text-slate-900 uppercase tracking-wider border-b border-slate-100 pb-3 flex items-center gap-2">
          <Scale className="w-4 h-4 text-blue-600" /> Parámetros de la Actualización Monetaria
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2 md:col-span-2">
            <Label className="text-xs font-semibold text-slate-700">Valor Histórico de Origen ($) *</Label>
            <Input
              type="number"
              value={valorHistorico}
              onChange={(e) => setValorHistorico(e.target.value)}
              placeholder="Ej: 25.000.000"
              className="bg-white border-slate-300 text-slate-900 rounded-xl"
            />
          </div>
          <div className="space-y-2">
            <Label className="text-xs font-semibold text-slate-700">Fecha / Mes de Origen *</Label>
            <Input
              type="month"
              value={fechaOrigen}
              onChange={(e) => setFechaOrigen(e.target.value)}
              className="bg-white border-slate-300 text-slate-900 rounded-xl"
            />
          </div>
          <div className="space-y-2">
            <Label className="text-xs font-semibold text-slate-700">Fecha / Mes de Corte *</Label>
            <Input
              type="month"
              value={fechaCorte}
              onChange={(e) => setFechaCorte(e.target.value)}
              className="bg-white border-slate-300 text-slate-900 rounded-xl"
            />
          </div>
        </div>

        {/* Modo Avanzado Toggle */}
        <div className="border-t border-slate-100 pt-4 space-y-3">
          <div className="flex items-center space-x-2">
            <input
              type="checkbox"
              id="modoAvanzadoToggle"
              checked={modoAvanzado}
              onChange={(e) => setModoAvanzado(e.target.checked)}
              className="rounded border-slate-300 text-blue-600 focus:ring-blue-500"
            />
            <Label htmlFor="modoAvanzadoToggle" className="text-xs font-bold text-slate-700 cursor-pointer">
              Modo Avanzado: Ingresar Índices IPC Manualmente
            </Label>
          </div>

          {modoAvanzado && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-2">
              <div className="space-y-2">
                <Label className="text-xs font-semibold text-slate-700">IPC Inicial Personalizado</Label>
                <Input
                  type="number"
                  value={ipcInicialCustom}
                  onChange={(e) => setIpcInicialCustom(e.target.value)}
                  placeholder="Ej: 106.1"
                  className="bg-white border-slate-300 text-slate-900 rounded-xl"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-xs font-semibold text-slate-700">IPC Final Personalizado</Label>
                <Input
                  type="number"
                  value={ipcFinalCustom}
                  onChange={(e) => setIpcFinalCustom(e.target.value)}
                  placeholder="Ej: 156.4"
                  className="bg-white border-slate-300 text-slate-900 rounded-xl"
                />
              </div>
            </div>
          )}
        </div>

        <Button
          onClick={handleCalculate}
          className="w-full bg-blue-600 hover:bg-blue-700 text-white font-black py-6 rounded-2xl uppercase tracking-wider text-xs shadow-md"
        >
          <Scale className="w-4 h-4 mr-2" /> Actualizar Valor Monetario por IPC — 1 Crédito
        </Button>
      </div>

      {/* Result View */}
      {result && <CivilResultDisplay result={result} />}

      {/* FAQ Section */}
      {faqs.length > 0 && (
        <div className="bg-white rounded-2xl border border-slate-200 p-6 space-y-4 shadow-sm">
          <h3 className="text-sm font-black text-slate-900 uppercase tracking-wider flex items-center gap-2 border-b border-slate-100 pb-3">
            <HelpCircle className="w-4 h-4 text-blue-600" /> Preguntas Frecuentes de Indexación por IPC
          </h3>
          <div className="space-y-4">
            {faqs.map((faq, i) => (
              <div key={i} className="space-y-1">
                <h4 className="text-xs font-bold text-slate-900">{faq.question}</h4>
                <p className="text-xs text-slate-600 leading-relaxed">{faq.answer}</p>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// 2. Liquidación Cuota Alimentaria
export const CuotaAlimentariaForm: React.FC = () => {
  const [alimentado, setAlimentado] = useState("");
  const [alimentante, setAlimentante] = useState("");
  const [valorCuota, setValorCuota] = useState("800000");
  const [fechaInicio, setFechaInicio] = useState("2024-01-01");
  const [fechaCorte, setFechaCorte] = useState("2026-08-01");
  const [result, setResult] = useState<CalculationResult | null>(null);

  const handleCalculate = async () => {
    const success = await deductCalculatorCredit();
    if (!success) return;

    const v = parseFloat(valorCuota) || 0;
    const d1 = new Date(fechaInicio);
    const d2 = new Date(fechaCorte);
    const meses = Math.max(1, Math.round((d2.getTime() - d1.getTime()) / (1000 * 60 * 60 * 24 * 30.44)));

    setResult(calcularCuotaAlimentaria(meses, v));
  };

  const faqs = CALCULATOR_FAQS["cuota-alimentaria"] || [];

  return (
    <div className="space-y-8">
      {/* Explanatory Key Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-slate-50 rounded-2xl border border-slate-200 p-4 space-y-1">
          <p className="text-xs font-black text-slate-900 uppercase tracking-wider">Ley 1098 de 2006</p>
          <p className="text-xs text-slate-600 leading-relaxed">
            Código de la Infancia y la Adolescencia prevé intereses de mora y actualización de la cuota.
          </p>
        </div>
        <div className="bg-slate-50 rounded-2xl border border-slate-200 p-4 space-y-1">
          <p className="text-xs font-black text-slate-900 uppercase tracking-wider">Incremento Anual</p>
          <p className="text-xs text-slate-600 leading-relaxed">
            Reajuste anual según incremento pactado en acta de conciliación o IPC/SMLMV.
          </p>
        </div>
        <div className="bg-slate-50 rounded-2xl border border-slate-200 p-4 space-y-1">
          <p className="text-xs font-black text-slate-900 uppercase tracking-wider">Proceso Ejecutivo</p>
          <p className="text-xs text-slate-600 leading-relaxed">
            Certificación útil para promover ejecutivo de alimentos en juzgados de familia.
          </p>
        </div>
      </div>

      {/* Main Form */}
      <div className="bg-white rounded-2xl border border-slate-200 p-6 space-y-6 shadow-sm">
        <h3 className="text-sm font-black text-slate-900 uppercase tracking-wider border-b border-slate-100 pb-3 flex items-center gap-2">
          <Scale className="w-4 h-4 text-blue-600" /> Parámetros del Proceso de Alimentos
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label className="text-xs font-semibold text-slate-700">Demandante (Alimentado)</Label>
            <Input
              type="text"
              value={alimentado}
              onChange={(e) => setAlimentado(e.target.value)}
              placeholder="Nombre del alimentado"
              className="bg-white border-slate-300 text-slate-900 rounded-xl"
            />
          </div>
          <div className="space-y-2">
            <Label className="text-xs font-semibold text-slate-700">Demandado (Alimentante)</Label>
            <Input
              type="text"
              value={alimentante}
              onChange={(e) => setAlimentante(e.target.value)}
              placeholder="Nombre del alimentante"
              className="bg-white border-slate-300 text-slate-900 rounded-xl"
            />
          </div>
          <div className="space-y-2 md:col-span-2">
            <Label className="text-xs font-semibold text-slate-700">Valor Mensual de la Cuota ($) *</Label>
            <Input
              type="number"
              value={valorCuota}
              onChange={(e) => setValorCuota(e.target.value)}
              placeholder="Ej: 800.000"
              className="bg-white border-slate-300 text-slate-900 rounded-xl"
            />
          </div>
          <div className="space-y-2">
            <Label className="text-xs font-semibold text-slate-700">Fecha de Inicio *</Label>
            <Input
              type="date"
              value={fechaInicio}
              onChange={(e) => setFechaInicio(e.target.value)}
              className="bg-white border-slate-300 text-slate-900 rounded-xl"
            />
          </div>
          <div className="space-y-2">
            <Label className="text-xs font-semibold text-slate-700">Fecha de Corte *</Label>
            <Input
              type="date"
              value={fechaCorte}
              onChange={(e) => setFechaCorte(e.target.value)}
              className="bg-white border-slate-300 text-slate-900 rounded-xl"
            />
          </div>
        </div>

        <Button
          onClick={handleCalculate}
          className="w-full bg-blue-600 hover:bg-blue-700 text-white font-black py-6 rounded-2xl uppercase tracking-wider text-xs shadow-md"
        >
          <Calculator className="w-4 h-4 mr-2" /> Liquidar Deuda de Cuota Alimentaria — 1 Crédito
        </Button>
      </div>

      {/* Result View */}
      {result && <CivilResultDisplay result={result} />}

      {/* FAQ Section */}
      {faqs.length > 0 && (
        <div className="bg-white rounded-2xl border border-slate-200 p-6 space-y-4 shadow-sm">
          <h3 className="text-sm font-black text-slate-900 uppercase tracking-wider flex items-center gap-2 border-b border-slate-100 pb-3">
            <HelpCircle className="w-4 h-4 text-blue-600" /> Preguntas Frecuentes de Cuota Alimentaria
          </h3>
          <div className="space-y-4">
            {faqs.map((faq, i) => (
              <div key={i} className="space-y-1">
                <h4 className="text-xs font-bold text-slate-900">{faq.question}</h4>
                <p className="text-xs text-slate-600 leading-relaxed">{faq.answer}</p>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// 3. Cánones de Arrendamiento
export const CanonesArrendamientoForm: React.FC = () => {
  const [tipoArrendamiento, setTipoArrendamiento] = useState("vivienda");
  const [valorCanon, setValorCanon] = useState("1500000");
  const [fechaPrimerCanon, setFechaPrimerCanon] = useState("2024-01-01");
  const [fechaCorte, setFechaCorte] = useState("2026-08-01");
  const [result, setResult] = useState<CalculationResult | null>(null);

  const handleCalculate = async () => {
    const success = await deductCalculatorCredit();
    if (!success) return;

    const v = parseFloat(valorCanon) || 0;
    const d1 = new Date(fechaPrimerCanon);
    const d2 = new Date(fechaCorte);
    const meses = Math.max(1, Math.round((d2.getTime() - d1.getTime()) / (1000 * 60 * 60 * 24 * 30.44)));

    setResult(calcularCanonesArrendamiento(meses, v));
  };

  const faqs = CALCULATOR_FAQS["canones-arrendamiento"] || [];

  return (
    <div className="space-y-8">
      {/* Explanatory Key Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-slate-50 rounded-2xl border border-slate-200 p-4 space-y-1">
          <p className="text-xs font-black text-slate-900 uppercase tracking-wider">Ley 820 de 2003</p>
          <p className="text-xs text-slate-600 leading-relaxed">
            Regula los cánones de arrendamiento de vivienda urbana e incremento anual máximo (100% IPC).
          </p>
        </div>
        <div className="bg-slate-50 rounded-2xl border border-slate-200 p-4 space-y-1">
          <p className="text-xs font-black text-slate-900 uppercase tracking-wider">Intereses Moratorios</p>
          <p className="text-xs text-slate-600 leading-relaxed">
            6% anual CC (vivienda) o Tasa de Usura comercial para locales según pacto contractual.
          </p>
        </div>
        <div className="bg-slate-50 rounded-2xl border border-slate-200 p-4 space-y-1">
          <p className="text-xs font-black text-slate-900 uppercase tracking-wider">Restitución de Inmueble</p>
          <p className="text-xs text-slate-600 leading-relaxed">
            Desglose apto para anexo de demanda ejecutiva y restitutoria ante juzgados civiles.
          </p>
        </div>
      </div>

      {/* Main Form */}
      <div className="bg-white rounded-2xl border border-slate-200 p-6 space-y-6 shadow-sm">
        <h3 className="text-sm font-black text-slate-900 uppercase tracking-wider border-b border-slate-100 pb-3 flex items-center gap-2">
          <Scale className="w-4 h-4 text-blue-600" /> Parámetros del Contrato de Arrendamiento
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2 md:col-span-2">
            <Label className="text-xs font-semibold text-slate-700">Tipo de Arrendamiento *</Label>
            <Select value={tipoArrendamiento} onValueChange={setTipoArrendamiento}>
              <SelectTrigger className="bg-white border-slate-300 text-slate-900 rounded-xl"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-white border-slate-200 text-slate-900">
                <SelectItem value="vivienda">Vivienda urbana (Ley 820 / 6% EA Art. 1617 CC)</SelectItem>
                <SelectItem value="comercial">Comercial (Tasa de usura variable)</SelectItem>
                <SelectItem value="pacto">Pacto expreso de mora (Usura máxima)</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2 md:col-span-2">
            <Label className="text-xs font-semibold text-slate-700">Valor del Canon Mensual ($) *</Label>
            <Input
              type="number"
              value={valorCanon}
              onChange={(e) => setValorCanon(e.target.value)}
              placeholder="Ej: 1.500.000"
              className="bg-white border-slate-300 text-slate-900 rounded-xl"
            />
          </div>
          <div className="space-y-2">
            <Label className="text-xs font-semibold text-slate-700">Fecha del Primer Canon Vencido *</Label>
            <Input
              type="date"
              value={fechaPrimerCanon}
              onChange={(e) => setFechaPrimerCanon(e.target.value)}
              className="bg-white border-slate-300 text-slate-900 rounded-xl"
            />
          </div>
          <div className="space-y-2">
            <Label className="text-xs font-semibold text-slate-700">Fecha de Corte *</Label>
            <Input
              type="date"
              value={fechaCorte}
              onChange={(e) => setFechaCorte(e.target.value)}
              className="bg-white border-slate-300 text-slate-900 rounded-xl"
            />
          </div>
        </div>

        <Button
          onClick={handleCalculate}
          className="w-full bg-blue-600 hover:bg-blue-700 text-white font-black py-6 rounded-2xl uppercase tracking-wider text-xs shadow-md"
        >
          <Calculator className="w-4 h-4 mr-2" /> Liquidar Cánones de Arrendamiento en Mora — 1 Crédito
        </Button>
      </div>

      {/* Result View */}
      {result && <CivilResultDisplay result={result} />}

      {/* FAQ Section */}
      {faqs.length > 0 && (
        <div className="bg-white rounded-2xl border border-slate-200 p-6 space-y-4 shadow-sm">
          <h3 className="text-sm font-black text-slate-900 uppercase tracking-wider flex items-center gap-2 border-b border-slate-100 pb-3">
            <HelpCircle className="w-4 h-4 text-blue-600" /> Preguntas Frecuentes de Arrendamientos
          </h3>
          <div className="space-y-4">
            {faqs.map((faq, i) => (
              <div key={i} className="space-y-1">
                <h4 className="text-xs font-bold text-slate-900">{faq.question}</h4>
                <p className="text-xs text-slate-600 leading-relaxed">{faq.answer}</p>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// 4. Mora Propiedad Horizontal
export const MoraPHForm: React.FC = () => {
  const [valorCuota, setValorCuota] = useState("350000");
  const [primerVencimiento, setPrimerVencimiento] = useState("2024-01-05");
  const [fechaCorte, setFechaCorte] = useState("2026-08-01");
  const [result, setResult] = useState<CalculationResult | null>(null);

  const handleCalculate = async () => {
    const success = await deductCalculatorCredit();
    if (!success) return;

    const v = parseFloat(valorCuota) || 0;
    const d1 = new Date(primerVencimiento);
    const d2 = new Date(fechaCorte);
    const meses = Math.max(1, Math.round((d2.getTime() - d1.getTime()) / (1000 * 60 * 60 * 24 * 30.44)));

    setResult(calcularMoraPH(meses, v));
  };

  const faqs = CALCULATOR_FAQS["mora-ph"] || [];

  return (
    <div className="space-y-8">
      {/* Explanatory Key Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-slate-50 rounded-2xl border border-slate-200 p-4 space-y-1">
          <p className="text-xs font-black text-slate-900 uppercase tracking-wider">Ley 675 de 2001</p>
          <p className="text-xs text-slate-600 leading-relaxed">
            Artículo 30 establece tasa moratoria equivalente a 1.5 veces el interés bancario corriente.
          </p>
        </div>
        <div className="bg-slate-50 rounded-2xl border border-slate-200 p-4 space-y-1">
          <p className="text-xs font-black text-slate-900 uppercase tracking-wider">Cuotas de Administración</p>
          <p className="text-xs text-slate-600 leading-relaxed">
            Incluye cuotas ordinarias y extraordinarias aprobadas en asamblea de copropietarios.
          </p>
        </div>
        <div className="bg-slate-50 rounded-2xl border border-slate-200 p-4 space-y-1">
          <p className="text-xs font-black text-slate-900 uppercase tracking-wider">Título Ejecutivo</p>
          <p className="text-xs text-slate-600 leading-relaxed">
            Certificación expedida por el administrador presta mérito ejecutivo (Art. 48 Ley 675).
          </p>
        </div>
      </div>

      {/* Main Form */}
      <div className="bg-white rounded-2xl border border-slate-200 p-6 space-y-6 shadow-sm">
        <h3 className="text-sm font-black text-slate-900 uppercase tracking-wider border-b border-slate-100 pb-3 flex items-center gap-2">
          <Scale className="w-4 h-4 text-blue-600" /> Parámetros de Propiedad Horizontal
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2 md:col-span-2">
            <Label className="text-xs font-semibold text-slate-700">Valor Cuota Mensual ($) *</Label>
            <Input
              type="number"
              value={valorCuota}
              onChange={(e) => setValorCuota(e.target.value)}
              placeholder="Ej: 350.000"
              className="bg-white border-slate-300 text-slate-900 rounded-xl"
            />
          </div>
          <div className="space-y-2">
            <Label className="text-xs font-semibold text-slate-700">Primer Vencimiento en Mora *</Label>
            <Input
              type="date"
              value={primerVencimiento}
              onChange={(e) => setPrimerVencimiento(e.target.value)}
              className="bg-white border-slate-300 text-slate-900 rounded-xl"
            />
          </div>
          <div className="space-y-2">
            <Label className="text-xs font-semibold text-slate-700">Fecha de Corte *</Label>
            <Input
              type="date"
              value={fechaCorte}
              onChange={(e) => setFechaCorte(e.target.value)}
              className="bg-white border-slate-300 text-slate-900 rounded-xl"
            />
          </div>
        </div>

        <Button
          onClick={handleCalculate}
          className="w-full bg-blue-600 hover:bg-blue-700 text-white font-black py-6 rounded-2xl uppercase tracking-wider text-xs shadow-md"
        >
          <Calculator className="w-4 h-4 mr-2" /> Liquidar Cuotas PH en Mora (Ley 675/2001) — 1 Crédito
        </Button>
      </div>

      {/* Result View */}
      {result && <CivilResultDisplay result={result} />}

      {/* FAQ Section */}
      {faqs.length > 0 && (
        <div className="bg-white rounded-2xl border border-slate-200 p-6 space-y-4 shadow-sm">
          <h3 className="text-sm font-black text-slate-900 uppercase tracking-wider flex items-center gap-2 border-b border-slate-100 pb-3">
            <HelpCircle className="w-4 h-4 text-blue-600" /> Preguntas Frecuentes de Propiedad Horizontal
          </h3>
          <div className="space-y-4">
            {faqs.map((faq, i) => (
              <div key={i} className="space-y-1">
                <h4 className="text-xs font-bold text-slate-900">{faq.question}</h4>
                <p className="text-xs text-slate-600 leading-relaxed">{faq.answer}</p>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

const CivilResultDisplay: React.FC<{ result: CalculationResult }> = ({ result }) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    const text = `--- INFORME DE LIQUIDACIÓN CIVIL / FAMILIA ---
${result.title}
${result.summary}

TOTAL CONSOLIDADO: $${result.total.toLocaleString("es-CO", { maximumFractionDigits: 0 })} COP

DESGLOSE:
${result.breakdown.map((b) => `- ${b.label}: ${typeof b.value === "number" ? "$" + b.value.toLocaleString("es-CO") : b.value}`).join("\n")}

CITA NORMATIVA:
${result.normativa.map((n) => `- ${n}`).join("\n")}

Generado en Mi Asistente Legalia`;

    navigator.clipboard.writeText(text);
    setCopied(true);
    toast.success("Informe procesal copiado");
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="p-6 rounded-2xl bg-blue-50/60 border border-blue-200 space-y-6 text-slate-900 shadow-sm animate-in fade-in">
      <div className="flex justify-between items-start">
        <div>
          <span className="text-[11px] font-extrabold text-blue-800 uppercase tracking-wider">
            Resumen Consolidado de la Liquidación
          </span>
          <div className="text-3xl md:text-4xl font-black text-slate-900 mt-1">
            ${result.total.toLocaleString("es-CO", { maximumFractionDigits: 0 })} COP
          </div>
          <p className="text-xs text-slate-600 mt-1">{result.summary}</p>
        </div>
        <Button onClick={handleCopy} variant="outline" size="sm" className="bg-white border-slate-300 text-slate-700 hover:bg-slate-50 font-bold">
          {copied ? <Check className="w-4 h-4 text-blue-600 mr-1" /> : <Copy className="w-4 h-4 mr-1" />}
          {copied ? "Copiado!" : "Copiar Informe"}
        </Button>
      </div>

      {/* Breakdown Table */}
      <div className="space-y-2">
        <h4 className="text-xs font-bold text-slate-900 uppercase tracking-wider flex items-center gap-1.5">
          <BookOpen className="w-4 h-4 text-blue-700" /> Desglose de Conceptos
        </h4>
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden divide-y divide-slate-100">
          {result.breakdown.map((b, i) => (
            <div key={i} className="p-3.5 flex justify-between text-xs">
              <span className="text-slate-600 font-medium">{b.label}</span>
              <span className="font-bold text-slate-900">
                {typeof b.value === "number" ? `$${b.value.toLocaleString("es-CO")}` : b.value}
              </span>
            </div>
          ))}
        </div>
      </div>

      {/* Normativa */}
      <div className="p-4 bg-white rounded-xl border border-slate-200 space-y-2">
        <h5 className="text-xs font-bold text-blue-800 uppercase tracking-wider">Fundamentación Legal</h5>
        <ul className="text-xs text-slate-600 list-disc pl-4 space-y-1">
          {result.normativa.map((n, i) => (
            <li key={i}>{n}</li>
          ))}
        </ul>
      </div>
    </div>
  );
};
